import { Box, FormControlLabel, Stack, Switch, Typography } from '@mui/material';
import { t } from 'i18next';
import { ComponentLoader } from '../../../../common/components/ComponentLoader';
import { TrendsChart } from './trends/TrendsChart';
import { useEffect, useState } from 'react';
import { SEVERITY_COLORS, Severity } from '../../../../common/enums/Severity';
import { PieChart } from '../../../../common/inventory/components/PieChart';
import { SeverityData } from './SeverityData';
import { TrendData } from '../../../../common/dashboards/models/TrendData';
import { formatRange } from '../../utils';
import { TrendDataScale } from '../../../../common/dashboards/models/TrendDataScale';
import { DashboardExportState } from '../../../../exports/triggers/states/DashboardExportState';
import { TrendsOverview as TrendsOverviewSkeleton } from '../skeletons/Overview/TrendsOverview';
import { DateRange } from '../../../../common/dashboards/types';

type Props = {
  trends: TrendData[];
  summaryTitle: string;
  loading: boolean;
  initialLoad: boolean;
  onUpdateDateRange: (dateRange: DateRange) => void;
};

const SEVERITY_LEVELS = 4;

type DataStats = { high: number; medium: number; low: number; passed: number };

export const TrendsOverview = ({ trends, loading, initialLoad, summaryTitle, onUpdateDateRange }: Props) => {
  const exportState = DashboardExportState.getInstance();
  const [activeIndex, setActiveIndex] = useState<number>();
  const [includePassed, setIncludePassed] = useState(true);
  const [selectedTrend, setSelectedTrend] = useState<TrendData | null>(null);

  useEffect(() => {
    // If trends change, we want to remove the selected trend in favor of displaying all data
    setSelectedTrend(null);
  }, [trends]);

  useEffect(() => {
    exportState.setIncludePassed(includePassed);
  }, [includePassed, exportState]);

  // if we are doing an initial load, show skeleton
  if (initialLoad) {
    return <TrendsOverviewSkeleton />;
  }

  let dataStats: DataStats;
  if (selectedTrend !== null) {
    dataStats = {
      high: selectedTrend.getHighInstances(),
      low: selectedTrend.getMediumInstances(),
      medium: selectedTrend.getLowInstances(),
      passed: includePassed ? selectedTrend.getPassedInstances() : 0,
    };
  } else {
    dataStats = trends.reduce(
      (stats, trend) => {
        const passed = includePassed ? stats.passed + trend.getPassedInstances() : 0;

        return {
          high: stats.high + trend.getHighInstances(),
          medium: stats.medium + trend.getMediumInstances(),
          low: stats.low + trend.getLowInstances(),
          passed,
        };
      },
      { high: 0, medium: 0, low: 0, passed: 0 }
    );
  }

  const chartData = [
    { name: t('high'), amount: dataStats.high, color: SEVERITY_COLORS[Severity.HIGH].main },
    { name: t('medium'), amount: dataStats.medium, color: SEVERITY_COLORS[Severity.MED].main },
    { name: t('low'), amount: dataStats.low, color: SEVERITY_COLORS[Severity.LOW].main },
    { name: t('passed'), amount: dataStats.passed, color: SEVERITY_COLORS[Severity.PASSED].main },
    { name: t('blank'), amount: 0, color: SEVERITY_COLORS[Severity.NO_WARNING].main },
  ];

  const isEmptyChart = dataStats.high + dataStats.low + dataStats.medium + dataStats.passed === 0;
  if (isEmptyChart) {
    // Set blank to 1 so that it renders a blank chart.
    chartData[chartData.length - 1].amount = 1;
  }

  const graphTitle = selectedTrend !== null ? formatRange([selectedTrend.getFrom(), selectedTrend.getTo()], TrendDataScale.DAY) : summaryTitle;

  const chartTitle = t('a11y.organization.dashboard.overviewChart', {
    high: dataStats.high,
    medium: dataStats.medium,
    low: dataStats.low,
    passed: dataStats.passed,
  });

  return (
    <ComponentLoader loading={loading}>
      <Stack
        sx={{
          backgroundColor: 'white',
          boxShadow: 2,
          borderRadius: '4px',
          flexDirection: { xs: 'column', lg: 'row' },
          flexWrap: { xs: 'wrap', lg: 'nowrap' },
          borderTopRightRadius: { xs: '16px', lg: '34px' },
          '& > *': { flex: '0 0 auto' },
        }}
        alignItems="center"
        justifyContent="space-between"
      >
        <Box sx={{ width: { xs: '100%', lg: '60%' }, px: 2, pt: 2 }}>
          <Stack direction="row" justifyContent="space-between" sx={{ pl: 2, mb: 1.5 }}>
            <Typography variant="overline" sx={{ fontWeight: 500, color: 'text.secondary' }}>
              {t('organizationDashboard.instanceSummary')}
            </Typography>
            <FormControlLabel
              control={<Switch checked={includePassed} onChange={(event) => setIncludePassed(event.target.checked)} name="include-passed" />}
              label={
                <Typography variant="body2" sx={{ color: 'text.primary' }}>
                  {t('organizationDashboard.includePassedInstances')}
                </Typography>
              }
            />
          </Stack>
          <TrendsChart
            title={t('a11y.organization.dashboard.trendsChart', { range: graphTitle })}
            trends={trends}
            includePassed={includePassed}
            onUpdateDateRange={(dateRange) => onUpdateDateRange(dateRange)}
            onSelectTrend={(trend) => setSelectedTrend(trend)}
          />
        </Box>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'center', sm: 'start' }}
          justifyContent="space-evenly"
          spacing={1}
          sx={{ flex: '1', '& > *': { flex: '0 0 40%' } }}
        >
          <Box sx={{ p: 2, pb: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '16rem' }}>
            <Typography variant="overline" sx={{ fontWeight: 500, color: 'text.secondary' }}>
              {graphTitle}
            </Typography>
            <Box>
              <PieChart
                title={chartTitle}
                data={chartData}
                radius={90}
                chartWidth={30}
                onMouseEnter={(index) => setActiveIndex(index < SEVERITY_LEVELS ? index : undefined)}
                onMouseLeave={() => setActiveIndex(undefined)}
                activeIndex={activeIndex}
                total={isEmptyChart ? 0 : null}
              />
            </Box>
          </Box>
          <Box sx={{ minWidth: '10rem', pt: 4, pr: 1 }}>
            <SeverityData
              {...dataStats}
              onElementSelect={(idx?: number) => setActiveIndex(idx)}
              activeIndex={activeIndex}
              showPassed={includePassed}
            ></SeverityData>
          </Box>
        </Stack>
      </Stack>
    </ComponentLoader>
  );
};
